import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, Output, ViewChild } from "@angular/core";
import { TranslateService } from "../../util/translate.service";
import { CommonModule } from "@angular/common";
import { AngularSvgIconModule } from "angular-svg-icon";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TooltipModule } from "primeng/tooltip";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { ApeToastComponent } from "../ape-toast/ape-toast.component";
import { ApeDownloadButtonService, DownloadStatus } from "./ape-download-button.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ApeButtonComponent } from "../ape-button/ape-button.component";
import { ApeLogoComponent } from "../ape-logo/ape-logo.component";
import { ApeSpinnerComponent } from "../ape-spinner/ape-spinner.component";

/**
 * Componente pulsante di download con spinner e tooltip integrati
 *
 * CASI D'USO:
 * - Pulsanti di download che richiedono feedback visivo durante l'operazione
 * - Download con stato di caricamento e notifiche di successo
 */
@Component({
  selector: "ape-download-button",
  standalone: true,
  imports: [
    CommonModule, AngularSvgIconModule, ProgressSpinnerModule, TooltipModule, ApeToastComponent, TranslatePipe, ApeButtonComponent, ApeLogoComponent, ApeSpinnerComponent
  ],
  templateUrl: "./ape-download-button.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApeDownloadButtonComponent {
  @Input({ required: true })
  iconSrc: string = "";

  @Input()
  disabled: boolean = false;

  @Input()
  downloadId?: string;

  /**
   * Evento emesso quando si clicca sul pulsante di download.
   * Emette l'ID univoco del download generato dal servizio.
   */
  @Output()
  readonly downloadClick: EventEmitter<string> = new EventEmitter<string>();

  protected isDownloading: boolean = false;

  protected isModalVisible: boolean = false;

  protected isDownloadCompleted: boolean = false;

  @ViewChild(ApeToastComponent, { static: false }) private toastComponent?: ApeToastComponent;

  // Espone TRANSLATION_KEYS per il template
  protected readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  private readonly downloadService = inject(ApeDownloadButtonService);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly translateService = inject(TranslateService);

  private modalTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    let wasDownloading = false;

    // Sottoscrivi allo stato del download
    this.downloadService.downloadState$
      .pipe(takeUntilDestroyed())
      .subscribe(status => {
        const isCurrentlyDownloading = status === DownloadStatus.DOWNLOADING;
        const isAborted = status === DownloadStatus.ABORTED;

        // Gestione della modale per download lunghi
        if (isCurrentlyDownloading && !wasDownloading) {
          // Inizio del download - pulisci eventuali timer residui e imposta nuovo timer per 5 secondi
          this.clearModalTimeout();
          this.isModalVisible = false;
          this.isDownloadCompleted = false;
          this.modalTimeout = setTimeout(() => {
            // Verifica ancora che il download sia ancora in corso prima di aprire la modale
            if (this.downloadService.isDownloading()) {
              this.isModalVisible = true;
              this.cdr.markForCheck();
            }
          }, 5000);
        } else if (!isCurrentlyDownloading && wasDownloading) {
          // Fine del download - pulisci il timer solo se il download è terminato
          this.clearModalTimeout();

          if (isAborted) {
            // Se è stato abortito, resetta lo stato visivo senza mostrare successo
            this.isModalVisible = false;
            this.isDownloadCompleted = false;
          } else {
            // Download completato con successo
            // Se la modale è visibile, aggiorna lo stato a completato
            if (this.isModalVisible) {
              this.isDownloadCompleted = true;
            } else {
              // Se la modale non è visibile (download completato prima dei 5 secondi), mostra solo il toast
              this.showSuccessToast();
            }
          }
        }

        // Aggiorna lo stato basandosi sul downloadId
        this.isDownloading = isCurrentlyDownloading;
        wasDownloading = isCurrentlyDownloading;

        // Forza il change detection per OnPush
        this.cdr.markForCheck();
      });
  }

  /**
   * Pulisce il timer della modale se presente
   */
  private clearModalTimeout(): void {
    if (this.modalTimeout) {
      clearTimeout(this.modalTimeout);
      this.modalTimeout = undefined;
    }
  }

  /**
   * Gestisce il click sul pulsante di download
   */
  protected onDownloadClick(): void {
    if (this.disabled || this.isDownloading) {
      return;
    }

    // Pulisci eventuali timer residui prima di avviare un nuovo download
    this.clearModalTimeout();
    this.isModalVisible = false;
    this.isDownloadCompleted = false;

    // Inizia il download tramite il service e ottieni ID
    const downloadId = this.downloadService.startDownload();

    // Emetti l'evento al parent con l'ID
    this.downloadClick.emit(downloadId);
  }

  /**
   * Mostra il toast di successo
   */
  private showSuccessToast(): void {
    if (this.toastComponent) {
      const title: string = this.translateService.instant(TRANSLATION_KEYS.downloadButton.DOWNLOAD_SUCCESS_TITLE);
      const detail: string = this.translateService.instant(TRANSLATION_KEYS.downloadButton.DOWNLOAD_SUCCESS_DETAIL);
      this.toastComponent.showToast("success", title, detail, "assets/icons/circle-check-big.svg");
    }
  }

  /**
   * Gestisce l'interruzione del download
   */
  protected onInterruptDownload(): void {
    // Pulisci il timer immediatamente per evitare che si apra la modale dopo l'annullamento
    this.clearModalTimeout();

    // Cancella il download nel service
    this.downloadService.cancelDownload();

    // Chiudi la modale e resetta lo stato
    this.isModalVisible = false;
    this.isDownloadCompleted = false;
    this.isDownloading = false;

    // Forza il change detection per aggiornare immediatamente la UI
    this.cdr.markForCheck();
  }

  /**
   * Gestisce la chiusura della modale
   */
  protected onCloseModal(): void {
    // Pulisci il timer quando si chiude la modale
    this.clearModalTimeout();

    this.isModalVisible = false;
    this.isDownloadCompleted = false;
    this.isDownloading = false;

    // Forza il change detection per aggiornare immediatamente la UI
    this.cdr.markForCheck();
  }

  /**
   * Verifica se il pulsante deve essere disabilitato
   */
  protected get isDisabled(): boolean {
    return this.disabled || this.isDownloading;
  }
}
